import React, { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Image, { UserProfilePhotoMissing } from './Image';
import { useContactsContext } from '../contexts/ContactsContext';
import { DeviceQueries, Separator, SeparatorMobile } from '../components/styled';
import { ContactsButton } from '../components/styled/ContactsButton';
import left_nav_logo from './assets/sunshine_logo.png';

import { TAppContact } from 'TProtocol/common/models';
import Spinner from 'react-bootstrap/Spinner';

interface Props {
  mobile: boolean;
  page: string;
  toggleMenu?: () => void;
}

function LeftNav({ mobile, page, toggleMenu }: Props) {
  const contactsContext = useContactsContext();
  const selfContact = contactsContext.selfContact;

  const onSelfContactClick = () => {
    if (selfContact && selfContact.name) {
      onSelected(selfContact);
    }
  };
  const onSelected = (contact: TAppContact) => {
    contactsContext.setSelectedContact(contact);
  };

  const handleSignOut = async () => {
    contactsContext.logout();
  };

  if (mobile) {
    return <LeftNavMobile>
      <LeftNavList>
        <LeftNavMobileListItem active={page === 'contacts'} onClick={toggleMenu}>
          <Link to="/contacts">Contact List</Link>
        </LeftNavMobileListItem>

        <SeparatorMobile/>

        <LeftNavMobileListItem active={page === 'review'} onClick={toggleMenu}>
          <Link to="/review">For Review</Link>
        </LeftNavMobileListItem>
        {/*<LeftNavMobileListItem active={page === 'birthday'} onClick={toggleMenu}>*/}
        {/*  <Link to="/birthday">Birthdays</Link>*/}
        {/*</LeftNavMobileListItem>*/}

        <SeparatorMobile/>

        <LeftNavMobileListItem active={page === 'add'} onClick={toggleMenu}>
          <Link to="/add">Add Contact</Link>
        </LeftNavMobileListItem>

        <SeparatorMobile/>

        <LeftNavMobileListItem active={page === 'support'} onClick={toggleMenu}>
          <Link to="/support">Customer Support</Link>
        </LeftNavMobileListItem>
        <LeftNavMobileListItem onClick={toggleMenu}>
          <a href="https://www.sunshine.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        </LeftNavMobileListItem>
        <LeftNavMobileListItem onClick={toggleMenu}>
          <a href="https://www.sunshine.com/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a>
        </LeftNavMobileListItem>

        <SeparatorMobile/>

        <LeftNavMobileListItem onClick={handleSignOut}>
          <Link to="/login">Sign Out</Link>
        </LeftNavMobileListItem>
      </LeftNavList>
    </LeftNavMobile>;
  }

  let forReviewBadge: ReactNode | null = null;
  if (!contactsContext.homepageLoaded) {
    forReviewBadge = <Spinner size="sm" animation="border"/>;
  } else if (contactsContext.LCM?.size ?? 0 > 0) {
    forReviewBadge = <ForReviewBadge>{contactsContext.LCM?.size}</ForReviewBadge>
  }

  return (
    <LeftNavDesktop>
      <NavLink to="/contacts">
        <LogoHeader alt="Sunshine" src={left_nav_logo}/>
      </NavLink>
      <LeftNavProfile>
        {selfContact && selfContact.name ? <>
            <LeftNavProfileTop>
              <Image src={selfContact.photoUrl ?? ''}/>
              <LeftNavNameWrapper>
                <LeftNavFirstName>{selfContact?.name.firstName}</LeftNavFirstName>
                <LeftNavName>{selfContact?.name.lastName}</LeftNavName>
              </LeftNavNameWrapper>
            </LeftNavProfileTop>
          </> :
          <div className="text-sm text-center mb-3">Loading...</div>
        }

        <LeftNavButtonWrapper>
          <ContactsButton disabled={!selfContact?.name} onClick={onSelfContactClick} type="button">
            View My Info
          </ContactsButton>
        </LeftNavButtonWrapper>

      </LeftNavProfile>
      <LeftNavList>
        <LeftNavListItem active={page === 'contacts'}>
          <Link to="/contacts">Contact List</Link> {contactsContext.contactsLoaded ? '' : <Spinner size="sm" animation="border"/>}
        </LeftNavListItem>

        <Separator/>

        <LeftNavListItem active={page === 'review'}>
          <Link to="/review">For Review</Link> {forReviewBadge}
        </LeftNavListItem>
        {/*<LeftNavListItem active={page === 'birthday'}>*/}
        {/*  <Link to="/birthday">Birthdays</Link>*/}
        {/*</LeftNavListItem>*/}

        <Separator/>

        <LeftNavListItem active={page === 'add'}>
          <Link to="/add">Add Contact</Link>
        </LeftNavListItem>
        <LeftNavListItem active={page === 'import'}>
          <Link to="/import">Import</Link>
        </LeftNavListItem>

        <Separator/>

        <LeftNavListItem active={page === 'support'}>
          <Link to="/support">Customer Support</Link>
        </LeftNavListItem>
        <LeftNavListItem>
          <a href="https://www.sunshine.com/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</a>
        </LeftNavListItem>
        <LeftNavListItem>
          <a href="https://www.sunshine.com/terms-of-service" target="_blank" rel="noreferrer">Terms of Service</a>
        </LeftNavListItem>

        <Separator/>

        <LeftNavListItem active={page === 'signout'} onClick={handleSignOut}>
          <Link to="/login">Sign Out</Link>
        </LeftNavListItem>
      </LeftNavList>
    </LeftNavDesktop>
  );
}

const LeftNavMobile = styled.div`
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  border-radius: 5px;
`;

const LeftNavList = styled.div`
  padding-top: 12px;
  padding-bottom: 12px;
`;

const LeftNavDesktop = styled.div`
  max-width: 280px;
  width: 100%;

  ${UserProfilePhotoMissing} {
    margin-bottom: 16px;
  }

  @media (${DeviceQueries.mobile}) {
    display: none;
  }
`;

const LogoHeader = styled.img`
  max-width: 100%;
  margin: 15px 0 25px;
`;

const LeftNavProfileTop = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 1rem;
`;

const LeftNavProfile = styled.div`
  width: 100%;
  font-size: 20px;
  line-height: 1.2;
  letter-spacing: 0.26px;
  color: #000000;
  margin-left: 15px;
  margin-right: 15px;
`;

const LeftNavNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
`;

const LeftNavName = styled.div`
  @media (${DeviceQueries.laptop}) {
    font-size: 16px;
  }
`;

const LeftNavFirstName = styled(LeftNavName)`
  font-weight: 500;
`;

const LeftNavButtonWrapper = styled.div`
  width: 90%;
  margin-right: auto;
  background-color: unset;

  ${ContactsButton} {
    width: 100%;
    margin-bottom: 40px;
  }
`;

const LeftNavListItem = styled.div`
  margin-left: 15px;
  font-size: 14px;
  margin-bottom: 16px;

  ${(props: { active?: boolean }) => props.active && css`
    a {
      font-weight: 500;
      color: #009bdf;
    }
  `}
`;

const LeftNavMobileListItem = styled.div`
  font-size: 14px;
  padding-bottom: 6px;
  padding-left: 12px;
  padding-right: 12px;

  ${(props: { active?: boolean }) => props.active && css`
    a {
      font-weight: 500;
      color: #009bdf;
    }
  `}
`;

const ForReviewBadge = styled.span`
  background-color: #ff3333;
  border-radius: 10px;
  font-size: 12px;
  color: white;
  padding: 2px 5px;
`

export default LeftNav;


